import React, { useState, useEffect } from "react";
import axios from "axios";
import { Container, Table, Button, Modal, Form } from "react-bootstrap";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

const AdminExecutiveLeaders = () => {
    const [leaders, setLeaders] = useState([]);
    const [imagePath, setImagePath] = useState("");
    const [showModal, setShowModal] = useState(false);
    const [formData, setFormData] = useState({
        title: "",
        role: "",
        description: "",
        image: null,
    });
    const [editId, setEditId] = useState(null);
    const [previewImage, setPreviewImage] = useState(null); // Preview image when editing

    // Fetch existing leaders
    const fetchLeaders = async () => {
        try {
            const response = await axios.get("https://api.biznetusa.com/api/all-executiveleaders");
            if (response.data.status === 200) {
                setLeaders(response.data.leaders);
                setImagePath(response.data.imagePath);
            } else {
                toast.error("Failed to fetch leaders.");
            }
        } catch (error) {
            console.error("Error fetching leaders:", error);
            toast.error("An error occurred while fetching leaders.");
        }
    };

    useEffect(() => {
        fetchLeaders();
    }, []);

    // Handle Store (Add)
    const handleStore = async () => {
        const formDataToSend = new FormData();
        formDataToSend.append("title", formData.title);
        formDataToSend.append("role", formData.role);
        formDataToSend.append("description", formData.description);
        if (formData.image) {
            formDataToSend.append("image", formData.image);
        }

        try {
            const response = await axios.post(
                "https://api.biznetusa.com/api/store-executiveleaders",
                formDataToSend,
                { headers: { "Content-Type": "multipart/form-data" } }
            );

            if (response.data.status === 200) {
                toast.success(response.data.message || "Leader added successfully.");
                fetchLeaders();
                handleCloseModal();
            } else {
                toast.error(response.data.message || "Failed to add leader.");
            }
        } catch (error) {
            console.error("Error adding leader:", error);
            toast.error("An error occurred while adding the leader.");
        }
    };

    // Handle Update (Edit Leader including Image)
    const handleUpdate = async () => {
        const formDataToSend = new FormData();
        formDataToSend.append("_method", "PUT"); // Laravel needs this when using FormData with a PUT request
        formDataToSend.append("title", formData.title);
        formDataToSend.append("role", formData.role);
        formDataToSend.append("description", formData.description);
        if (formData.image) {
            formDataToSend.append("image", formData.image);
        }
    
        try {
            const response = await axios.post(  // ✅ Use POST instead of PUT
                `https://api.biznetusa.com/api/update-executiveleaders/${editId}`,
                formDataToSend,
                { headers: { "Content-Type": "multipart/form-data" } }
            );
    
            if (response.data.status === 200) {
                toast.success(response.data.message || "Leader updated successfully.");
                fetchLeaders();
                handleCloseModal();
            } else {
                toast.error(response.data.message || "Failed to update leader.");
            }
        } catch (error) {
            console.error("Error updating leader:", error);
            toast.error("An error occurred while updating the leader.");
        }
    };
    

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (editId) {
            await handleUpdate();
        } else {
            await handleStore();
        }
    };

    // Handle Delete
    const handleDelete = async (id) => {
        try {
            const response = await axios.delete(
                `https://api.biznetusa.com/api/deleteexecutiveleaders/${id}`
            );
            if (response.data.status === 200) {
                fetchLeaders();
                toast.success("Leader deleted successfully.");
            } else {
                toast.error(response.data.message || "Failed to delete leader.");
            }
        } catch (error) {
            console.error("Error deleting leader:", error);
            toast.error("An error occurred while deleting the leader.");
        }
    };

    // Handle Edit (Pre-fill the form)
    const handleEdit = (leader) => {
        setEditId(leader.id);
        setFormData({
            title: leader.title,
            role: leader.role,
            description: leader.description,
            image: null, // Reset file input
        });
        setPreviewImage(`${imagePath}${leader.image}`); // Show existing image
        setShowModal(true);
    };

    // Handle Modal Close & Reset Form
    const handleCloseModal = () => {
        setShowModal(false);
        setEditId(null);
        setFormData({ title: "", role: "", description: "", image: null });
        setPreviewImage(null);
    };

    return (
        <Container className="py-4">
            <h2 className="text-center mb-4">Executive Leaders Management</h2>
            <Button variant="primary" className="mb-3" onClick={() => setShowModal(true)}>
                Add Executive Leader
            </Button>
            <Table striped bordered hover responsive>
                <thead>
                    <tr>
                        <th>#</th>
                        <th>Title</th>
                        <th>Role</th>
                        <th>Description</th>
                        <th>Image</th>
                        <th>Actions</th>
                    </tr>
                </thead>
                <tbody>
                    {leaders.map((leader, index) => (
                        <tr key={leader.id}>
                            <td>{index + 1}</td>
                            <td>{leader.title}</td>
                            <td>{leader.role}</td>
                            <td>{leader.description}</td>
                            <td>
                                <img
                                    src={`${imagePath}${leader.image}`}
                                    alt={leader.title}
                                    style={{ width: "80px", height: "auto" }}
                                />
                            </td>
                            <td>
                                <Button variant="info" className="me-2" onClick={() => handleEdit(leader)}>
                                    Edit
                                </Button>
                                <Button variant="danger" onClick={() => handleDelete(leader.id)}>
                                    Delete
                                </Button>
                            </td>
                        </tr>
                    ))}
                </tbody>
            </Table>

            {/* Modal for Adding/Editing */}
            <Modal show={showModal} onHide={handleCloseModal}>
                <Modal.Header closeButton>
                    <Modal.Title>{editId ? "Edit Leader" : "Add Leader"}</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <Form onSubmit={handleSubmit}>
                        <Form.Group className="mb-3">
                            <Form.Label>Title</Form.Label>
                            <Form.Control type="text" value={formData.title} required onChange={(e) => setFormData({ ...formData, title: e.target.value })} />
                        </Form.Group>
                        <Form.Group className="mb-3">
                            <Form.Label>Role</Form.Label>
                            <Form.Control type="text" value={formData.role} required onChange={(e) => setFormData({ ...formData, role: e.target.value })} />
                        </Form.Group>
                        <Form.Group className="mb-3">
                            <Form.Label>Description</Form.Label>
                            <Form.Control type="text" value={formData.description} required onChange={(e) => setFormData({ ...formData, description: e.target.value })} />
                        </Form.Group>
                        {previewImage && <img src={previewImage} alt="Preview" style={{ width: "100px", marginBottom: "10px" }} />}
                        <Form.Group className="mb-3">
                            <Form.Label>Image</Form.Label>
                            <Form.Control type="file" accept="image/*" onChange={(e) => setFormData({ ...formData, image: e.target.files[0] })} />
                        </Form.Group>
                        <Button variant="primary" type="submit">{editId ? "Update" : "Add"}</Button>
                    </Form>
                </Modal.Body>
            </Modal>
        </Container>
    );
};

export default AdminExecutiveLeaders;
